import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { api, formatError } from "@/lib/api";
import { OptionButton, optionState } from "@/components/QuizPlayer";
import { Loading, ErrorState } from "@/components/States";

export default function SampleQuiz() {
  const [qs, setQs] = useState(null);
  const [err, setErr] = useState("");
  const [idx, setIdx] = useState(0);
  const [fb, setFb] = useState({});

  const load = () => {
    setErr(""); setQs(null); setIdx(0); setFb({});
    api.get("/public/sample-quiz").then((r) => setQs(r.data)).catch((e) => setErr(formatError(e)));
  };
  useEffect(load, []);

  if (err) return <ErrorState message={err} onRetry={load} />;
  if (!qs) return <Loading label="Loading sample quiz..." />;
  const done = idx >= qs.length;
  const score = Object.values(fb).filter((f) => f.is_correct).length;

  if (done) {
    return (
      <div data-testid="sample-quiz-done" className="flex flex-col items-start gap-4 rounded-2xl bg-band p-8 text-white">
        <p className="text-sm uppercase tracking-wider text-[var(--brand-cyan)]">Sample complete</p>
        <h3 className="text-3xl font-extrabold">You scored {score} / {qs.length}</h3>
        <p className="max-w-md text-slate-300">Create a free account to generate unlimited AI quizzes in 5, 10 or 20 questions, track progress and build flashcard decks.</p>
        <div className="flex flex-wrap gap-3">
          <Button asChild className="rounded-full bg-aqua-solid hover:bg-aqua-solid-hover"><Link data-testid="sample-register-btn" to="/register">Create free account</Link></Button>
          <Button data-testid="sample-retry-btn" variant="outline" className="rounded-full border-white/30 bg-transparent text-white hover:bg-white/10 hover:text-white" onClick={load}>Try another set</Button>
        </div>
      </div>
    );
  }

  const q = qs[idx];
  const f = fb[q.id];
  const check = async (sel) => {
    if (f) return;
    try {
      const { data } = await api.post("/public/sample-quiz/check", { question_id: q.id, selected: sel });
      setFb((p) => ({ ...p, [q.id]: { ...data, selected: sel } }));
    } catch (e) { setErr(formatError(e)); }
  };

  return (
    <div data-testid="sample-quiz" className="rounded-2xl border border-line bg-surface p-6 shadow-[0_20px_50px_-24px_rgba(10,31,68,.25)] sm:p-8">
      <div className="mb-4 flex items-center justify-between text-xs">
        <span className="font-mono text-ink-3">Sample {idx + 1} / {qs.length}</span>
        <span className="rounded-full bg-aqua-soft px-2.5 py-1 font-medium text-aqua">{q.topic}</span>
      </div>
      <h3 data-testid="sample-question-text" className="mb-6 text-lg font-bold leading-snug text-ink">{q.question}</h3>
      <div className="flex flex-col gap-2.5">
        {q.options.map((o, i) => <OptionButton key={i} i={i} text={o} state={optionState(i, f)} disabled={!!f} onClick={() => check(i)} />)}
      </div>
      {f && (
        <div className="mt-5 fade-up">
          <p data-testid="sample-feedback" className="rounded-lg bg-canvas-2 p-4 text-sm text-ink-2"><b>{f.is_correct ? "Correct. " : "Not quite. "}</b>{f.explanation}</p>
          <Button data-testid="sample-next-btn" className="mt-4 rounded-full" onClick={() => setIdx(idx + 1)}>
            {idx === qs.length - 1 ? "See score" : "Next"} <ArrowRight className="ml-2 h-4 w-4" />
          </Button>
        </div>
      )}
    </div>
  );
}
